import { useState, useEffect, useCallback, useRef } from "react";
import { useSignalR } from "../Contexts/SignalRContext";
import apiClient from "../Helpers/apiClient";

// Define workflow stages
export const WORKFLOW_STAGES = {
    CALCULATE_PRICE: "calculate_price",
    GENERATE_MODEL: "generate_model",
    DOWNLOAD_AND_QUOTE: "download_and_quote",
};

const STORAGE_KEY = "tornadoGPTOfflineState";
const HANDLER_ID = "tornadoGPTOffline";

export const useTornadoGPTOfflineState = (QuoteId, quantity, showNotification) => {
    const {
        connection,
        connectionId,
        isConnected,
        registerGlobalHandler,
        unregisterGlobalHandler,
        getPendingMessages,
    } = useSignalR();

    // Component state
    const [currentStage, setCurrentStage] = useState(WORKFLOW_STAGES.CALCULATE_PRICE);
    const [activeStep, setActiveStep] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [priceBreakdown, setPriceBreakdown] = useState(null);
    const [viewerPath, setViewerPath] = useState(
        `${process.env.REACT_APP_API_URL}/TornadoGPTOfflineStdModel/output/1/result.svf`
    );
    const [downloadUrls, setDownloadUrls] = useState({
        pdf: "",
        dwg: "",
        dwg3d: "",
    });
    const [modelConfig, setModelConfig] = useState({
        modelType: "TGPTO.1430.2530",
        finishSurfaceLevel: 108,
        inletIL: 102,
        inletSize: 1500, // Change from connectionSize to inletSize
        inletQuantity: 1, // Add new field
        weirLayout: "Left",
        city: "",
        workItemId: "",
    });
    console.log("🛠️ TornadoGPTOffline: Initial modelConfig:", modelConfig);

    // Log modelConfig whenever it changes
    useEffect(() => {
        console.log("🛠️ TornadoGPTOffline: modelConfig updated:", modelConfig);
    }, [modelConfig]);

    // Store the latest QuoteId and quantity in state to ensure it's available for pending message processing
    const [storedQuoteId, setStoredQuoteId] = useState(QuoteId);
    const [storedQuantity, setStoredQuantity] = useState(quantity);

    // Update stored values when props change
    useEffect(() => {
        console.log("🔄 TornadoGPTOffline: Updating stored QuoteId and quantity:", { QuoteId, quantity });
        setStoredQuoteId(QuoteId);
        setStoredQuantity(quantity);
    }, [QuoteId, quantity]);

    // Use refs to maintain current values in async callbacks
    const currentStateRef = useRef({
        QuoteId: storedQuoteId,
        quantity: storedQuantity,
        totalPrice,
        modelConfig,
        currentStage,
        activeStep,
        isGenerating,
        viewerPath,
        downloadUrls,
        priceBreakdown,
    });

    // Update ref whenever state changes
    useEffect(() => {
        currentStateRef.current = {
            QuoteId: storedQuoteId,
            quantity: storedQuantity,
            totalPrice,
            modelConfig,
            currentStage,
            activeStep,
            isGenerating,
            viewerPath,
            downloadUrls,
            priceBreakdown,
        };
    }, [
        storedQuoteId,
        storedQuantity,
        totalPrice,
        modelConfig,
        currentStage,
        activeStep,
        isGenerating,
        viewerPath,
        downloadUrls,
        priceBreakdown,
    ]);

    // Save state to sessionStorage
    const saveStateToLocalStorage = useCallback(() => {
        const stateToSave = {
            currentStage: currentStateRef.current.currentStage,
            activeStep: currentStateRef.current.activeStep,
            viewerPath: currentStateRef.current.viewerPath,
            downloadUrls: currentStateRef.current.downloadUrls,
            modelConfig: currentStateRef.current.modelConfig,
            totalPrice: currentStateRef.current.totalPrice,
            priceBreakdown: currentStateRef.current.priceBreakdown,
            isGenerating: currentStateRef.current.isGenerating,
            storedQuoteId: currentStateRef.current.QuoteId,
            storedQuantity: currentStateRef.current.quantity,
            lastSaved: Date.now(),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
        console.log("💾 TornadoGPTOffline: State saved to sessionStorage:", {
            stage: stateToSave.currentStage,
            step: stateToSave.activeStep,
            isGenerating: stateToSave.isGenerating,
            workItemId: stateToSave.modelConfig?.workItemId,
            hasDownloadUrls: Object.values(stateToSave.downloadUrls).some((url) => !!url),
            quoteId: stateToSave.storedQuoteId,
            quantity: stateToSave.storedQuantity,
            timestamp: new Date(stateToSave.lastSaved).toISOString(),
        });
    }, []);

    // Restore state from sessionStorage
    const restoreStateFromLocalStorage = useCallback(() => {
        console.log("🔍 TornadoGPTOffline: Attempting to restore state from sessionStorage...");
        try {
            const savedState = sessionStorage.getItem(STORAGE_KEY);
            if (!savedState) {
                console.log("✅ TornadoGPTOffline: No saved state found");
                return false;
            }

            const parsedState = JSON.parse(savedState);

            // Check if the saved state is not too old (optional: 24 hours)
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours
            const age = Date.now() - parsedState.lastSaved;
            if (age > maxAge) {
                console.log(
                    `🗑️ TornadoGPTOffline: Saved state is too old (${Math.round(
                        age / 60000
                    )} minutes), starting fresh`
                );
                sessionStorage.removeItem(STORAGE_KEY);
                return false;
            }

            console.log("🔄 TornadoGPTOffline: Restoring state from sessionStorage:", {
                stage: parsedState.currentStage,
                step: parsedState.activeStep,
                isGenerating: parsedState.isGenerating,
                workItemId: parsedState.modelConfig?.workItemId,
                hasDownloadUrls: Object.values(parsedState.downloadUrls).some((url) => !!url),
                quoteId: parsedState.storedQuoteId,
                quantity: parsedState.storedQuantity,
                ageMinutes: Math.round(age / 60000),
                savedAt: new Date(parsedState.lastSaved).toISOString(),
            });

            setCurrentStage(parsedState.currentStage);
            setActiveStep(parsedState.activeStep);
            setViewerPath(parsedState.viewerPath);
            setDownloadUrls(parsedState.downloadUrls);
            setModelConfig(parsedState.modelConfig);
            setTotalPrice(parsedState.totalPrice);
            setPriceBreakdown(parsedState.priceBreakdown);
            setIsGenerating(parsedState.isGenerating);

            // Restore the stored QuoteId and quantity
            if (parsedState.storedQuoteId) {
                setStoredQuoteId(parsedState.storedQuoteId);
            }
            if (parsedState.storedQuantity) {
                setStoredQuantity(parsedState.storedQuantity);
            }

            console.log("✅ TornadoGPTOffline: State restored successfully");
            return true;
        } catch (error) {
            console.error("❌ TornadoGPTOffline: Error restoring state from sessionStorage:", error);
            sessionStorage.removeItem(STORAGE_KEY);
            return false;
        }
    }, []);

    // 1. REPLACED: Generate TornadoGPTOffline description PDF
    const generateTornadoGptOfflineDescriptionPdf = useCallback(
        async (workItemId) => {
            console.log("🎯 TornadoGPTOffline: Starting PDF generation for workItemId:", workItemId);
            console.log("📊 TornadoGPTOffline: Current state ref:", currentStateRef.current);

            // Check if we have the required data with retry logic
            if (!currentStateRef.current.QuoteId) {
                console.log(
                    "⚠️ TornadoGPTOffline: QuoteId is missing, will retry PDF generation in 2 seconds"
                );
                setTimeout(() => {
                    if (currentStateRef.current.QuoteId) {
                        console.log(
                            "🔄 TornadoGPTOffline: Retrying PDF generation with QuoteId:",
                            currentStateRef.current.QuoteId
                        );
                        generateTornadoGptOfflineDescriptionPdf(workItemId);
                    } else {
                        console.log(
                            "❌ TornadoGPTOffline: QuoteId still missing, proceeding without PDF generation"
                        );
                        showNotification(
                            "Model generated successfully, but PDF generation was skipped due to missing QuoteId. You can still download files and add to quote.",
                            "warning"
                        );
                        // Move to download stage anyway
                        setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
                        setActiveStep(2);
                        setIsGenerating(false);
                        sessionStorage.removeItem(STORAGE_KEY);
                    }
                }, 2000);
                return;
            }

            try {
                const inputdata = {
                    ...currentStateRef.current.modelConfig,
                    QuoteId: currentStateRef.current.QuoteId,
                    TemplateId: "892243f1-f2b2-f011-bbd3-000d3ad0ff6e",
                    dataverseProductPrice: currentStateRef.current.totalPrice || 0,
                    dataveserQuoteProductQuantity: currentStateRef.current.quantity || 1,
                    workItemId: workItemId,
                };

                console.log("📤 TornadoGPTOffline: Sending PDF generation request with data:", inputdata);
                const response = await apiClient.post(
                    // NOTE: Use the specific backend endpoint for TornadoGPT PDF generation
                    "/api/pdf/generate-tornadogpt-offline-description-pdf",
                    inputdata
                );

                console.log("✅ TornadoGPTOffline: PDF generation successful, moving to download stage");
                showNotification(
                    "Model and PDF generated successfully! You can now download files and add to quote.",
                    "success"
                );

                // Move to download stage
                setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
                setActiveStep(2);
                setIsGenerating(false);

                // Clear the saved state since we're done
                console.log("🧹 TornadoGPTOffline: Clearing saved state after successful completion");
                sessionStorage.removeItem(STORAGE_KEY);
            } catch (err) {
                console.error("❌ TornadoGPTOffline: PDF generation error:", err);
                console.error("❌ TornadoGPTOffline: Error details:", {
                    message: err.message,
                    response: err.response?.data,
                    status: err.response?.status,
                });

                // Check if it's a QuoteId validation error
                if (err.response?.status === 400 && err.response?.data?.errors?.QuoteId) {
                    console.log(
                        "🔄 TornadoGPTOffline: QuoteId validation error, will try to proceed without PDF"
                    );
                    showNotification(
                        "Model generated successfully, but PDF generation failed due to missing QuoteId. You can still download files and add to quote.",
                        "warning"
                    );
                } else {
                    showNotification(
                        err.response?.data?.message ||
                            "Failed to generate PDF, but model was created successfully",
                        "error"
                    );
                }

                // Always move to download stage if model generation was successful, even if PDF failed
                setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
                setActiveStep(2);
                setIsGenerating(false);
                sessionStorage.removeItem(STORAGE_KEY);
            }
        },
        [showNotification]
    );

    // Handle onComplete SignalR event
    const handleOnComplete = useCallback(() => {
        console.log("📨 TornadoGPTOffline: Received onComplete event");
        // onComplete doesn't do much in the current implementation
    }, []);

    // 2. UPDATED: Handle onComponents SignalR event
    const handleOnComponents = useCallback(
        async (message) => {
            console.log("📨 TornadoGPTOffline: Received onComponents event:", message);
            console.log("📊 TornadoGPTOffline: Current component state when processing:", {
                currentStage: currentStateRef.current.currentStage,
                isGenerating: currentStateRef.current.isGenerating,
                totalPrice: currentStateRef.current.totalPrice,
                QuoteId: currentStateRef.current.QuoteId,
                quantity: currentStateRef.current.quantity,
            });

            const baseUrl = process.env.REACT_APP_API_URL;
            const workitemId = message.split("Workitems/")[1].split("/")[0];

            const newViewerPath = `${baseUrl}/${message}`;

            // FIXED: Use the correct download endpoint pattern matching your C# controller
            const newDownloadUrls = {
                pdf: `${baseUrl}/api/aps/designautomation/tornadoGPTofflinedownload/pdf?workitemId=${workitemId}`,
                dwg: `${baseUrl}/api/aps/designautomation/tornadoGPTofflinedownload/dwg?workitemId=${workitemId}`,
                dwg3d: `${baseUrl}/api/aps/designautomation/tornadoGPTofflinedownload/dwg3d?workitemId=${workitemId}`,
            };

            console.log("🔄 TornadoGPTOffline: Updating state with new paths:", {
                workitemId,
                newViewerPath,
                newDownloadUrls,
            });

            // Update state
            setViewerPath(newViewerPath);
            setDownloadUrls(newDownloadUrls);
            setModelConfig((prev) => ({
                ...prev,
                workItemId: workitemId,
            }));

            // Save updated state immediately
            setTimeout(() => {
                console.log("💾 TornadoGPTOffline: Saving state after onComponents processing");
                saveStateToLocalStorage();
            }, 100);

            // CHANGED: Call the full PDF generation logic
            console.log("🎯 TornadoGPTOffline: Starting PDF generation...");
            await generateTornadoGptOfflineDescriptionPdf(workitemId);
        },
        [generateTornadoGptOfflineDescriptionPdf, saveStateToLocalStorage]
    );

    // Register/unregister SignalR handlers with type-aware message handling
    useEffect(() => {
        if (!isConnected) {
            console.log("⏳ TornadoGPTOffline: Waiting for SignalR connection...");
            return;
        }

        console.log("🔧 TornadoGPTOffline: Registering SignalR handlers");
        registerGlobalHandler("onComplete", HANDLER_ID, handleOnComplete);
        registerGlobalHandler("onComponents", HANDLER_ID, handleOnComponents);

        // Check for pending messages when the component mounts or reconnects
        const checkPendingMessages = () => {
            console.log("🔍 TornadoGPTOffline: Checking for pending messages...");
            console.log("📊 TornadoGPTOffline: Current QuoteId and quantity:", {
                QuoteId: currentStateRef.current.QuoteId,
                quantity: currentStateRef.current.quantity,
            });

            // Get pending messages for TornadoGPTOffline workitem type only
            const pendingComplete = getPendingMessages("onComplete", "tornadoGPTOffline");
            const pendingComponents = getPendingMessages("onComponents", "tornadoGPTOffline");

            console.log("📥 TornadoGPTOffline: Found pending messages:", {
                onComplete: pendingComplete.length,
                onComponents: pendingComponents.length,
            });

            // Process pending onComplete messages
            if (pendingComplete.length > 0) {
                console.log("🎯 TornadoGPTOffline: Processing pending onComplete messages");
                pendingComplete.forEach(({ data, timestamp, id }) => {
                    console.log(
                        `📨 TornadoGPTOffline: Processing onComplete message ${id} from ${new Date(
                            timestamp
                        ).toISOString()}`
                    );
                    handleOnComplete(data);
                });
            }

            // Process pending onComponents messages (most recent first)
            if (pendingComponents.length > 0) {
                console.log("🎯 TornadoGPTOffline: Processing pending onComponents messages");
                // Take the most recent message
                const mostRecent = pendingComponents[pendingComponents.length - 1];
                console.log(
                    `📨 TornadoGPTOffline: Processing most recent onComponents message ${
                        mostRecent.id
                    } from ${new Date(mostRecent.timestamp).toISOString()}`
                );

                // Ensure we have QuoteId before processing
                if (!currentStateRef.current.QuoteId) {
                    console.log(
                        "⚠️ TornadoGPTOffline: QuoteId not available yet, will retry processing in 2 seconds"
                    );
                    setTimeout(() => {
                        console.log(
                            "🔄 TornadoGPTOffline: Retrying onComponents processing with QuoteId:",
                            currentStateRef.current.QuoteId
                        );
                        if (currentStateRef.current.QuoteId) {
                            handleOnComponents(mostRecent.data);
                        } else {
                            console.log(
                                "❌ TornadoGPTOffline: QuoteId still not available, skipping pending message processing"
                            );
                        }
                    }, 2000);
                } else {
                    // Process immediately
                    handleOnComponents(mostRecent.data);
                }
            }

            if (pendingComplete.length === 0 && pendingComponents.length === 0) {
                console.log("✅ TornadoGPTOffline: No pending messages to process");
            }
        };

        // Check immediately
        checkPendingMessages();

        // Also check after a short delay to ensure handlers are fully registered
        const timeoutId = setTimeout(() => {
            console.log("🔍 TornadoGPTOffline: Second check for pending messages (after delay)");
            checkPendingMessages();
        }, 1000);

        // Final check after a longer delay to catch cases where QuoteId takes time to be set
        const finalTimeoutId = setTimeout(() => {
            console.log("🔍 TornadoGPTOffline: Final check for pending messages (after longer delay)");
            checkPendingMessages();
        }, 3000);

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(finalTimeoutId);
            console.log("🧹 TornadoGPTOffline: Unregistering SignalR handlers");
            unregisterGlobalHandler("onComplete", HANDLER_ID);
            unregisterGlobalHandler("onComponents", HANDLER_ID);
        };
    }, [
        isConnected,
        registerGlobalHandler,
        unregisterGlobalHandler,
        getPendingMessages,
        handleOnComplete,
        handleOnComponents,
    ]);

    // Auto-save state when relevant data changes
    useEffect(() => {
        if (currentStage !== WORKFLOW_STAGES.CALCULATE_PRICE || isGenerating) {
            saveStateToLocalStorage();
        }
    }, [
        currentStage,
        activeStep,
        viewerPath,
        downloadUrls,
        modelConfig,
        totalPrice,
        priceBreakdown,
        isGenerating,
        saveStateToLocalStorage,
    ]);

    // Stage transition functions
    const moveToGenerateStage = useCallback(() => {
        setCurrentStage(WORKFLOW_STAGES.GENERATE_MODEL);
        setActiveStep(1);
    }, []);

    const moveToDownloadStage = useCallback(() => {
        setCurrentStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);
        setActiveStep(2);
        setIsGenerating(false);
        sessionStorage.removeItem(STORAGE_KEY);
    }, []);

    // Reset workflow
    const resetWorkflow = useCallback(() => {
        setCurrentStage(WORKFLOW_STAGES.CALCULATE_PRICE);
        setActiveStep(0);
        setTotalPrice(0);
        setPriceBreakdown(null);
        setDownloadUrls({ pdf: "", dwg: "", dwg3d: "" });
        setViewerPath(`${process.env.REACT_APP_API_URL}/TornadoGPTOfflineStdModel/output/1/result.svf`);
        setModelConfig((prev) => ({ ...prev, workItemId: "" }));
        setIsGenerating(false);
        sessionStorage.removeItem(STORAGE_KEY);
        showNotification("Reset to start new configuration", "info");
    }, [showNotification]);

    return {
        // State
        currentStage,
        activeStep,
        isGenerating,
        totalPrice,
        priceBreakdown,
        viewerPath,
        downloadUrls,
        modelConfig,

        // Setters
        setCurrentStage,
        setActiveStep,
        setIsGenerating,
        setTotalPrice,
        setPriceBreakdown,
        setViewerPath,
        setDownloadUrls,
        setModelConfig,

        // Functions
        moveToGenerateStage,
        moveToDownloadStage,
        resetWorkflow,
        saveStateToLocalStorage,
        restoreStateFromLocalStorage,

        // SignalR status
        isSignalRConnected: isConnected,
        signalRConnectionId: connectionId,
    };
};
